import { ActionIcon, Box, Button, Group, SimpleGrid, Table, Text, Tooltip } from "@mantine/core"
import { ArrowUpRightIcon, InfoIcon } from "@phosphor-icons/react"
import React from "react"
import { useTranslation } from "react-i18next"
import { DotPill, TableFrame } from "../../delivery/atoms"
import { ListState } from "../../ui"
import classes from "./catalog.module.css"
import { daysUntil, describePromotion, formatDate, PROMO_KINDS, promoIcon, type PromoGroup, type PromoKind } from "./price-labels"

type Props = {
  groups: PromoGroup[]
  /** Header supplier picker, shared with the catalogue. */
  supplierPicker: React.ReactNode
  onOpenPromotion: (promotionId: string) => void
}

const ENDING_SOON_DAYS = 7

function Ends({ value }: Readonly<{ value: string | null }>) {
  const { t } = useTranslation("order")
  const days = daysUntil(value)
  if (days === null) return <Text fz="var(--nw-fz-body)">-</Text>
  const left = days <= 0 ? t("catalog.promotions.endsToday") : t("catalog.promotions.daysLeft", { count: days })
  return (
    <Box>
      {days <= ENDING_SOON_DAYS ? (
        <DotPill tone="red">{left}</DotPill>
      ) : (
        <Text fz="var(--nw-fz-body)" fw={600}>
          {left}
        </Text>
      )}
      <Text className={`${classes.meta} ${classes.mono}`}>{t("catalog.untilDate", { date: formatDate(value) })}</Text>
    </Box>
  )
}

/**
 * Promotion page (canvas OrdB_PromoPage): every promotion touching the
 * buyer's catalogue, grouped by type, with what is about to end on top.
 * Built from the catalogue's own price breakdowns; a buyer-wide promotion
 * endpoint does not exist yet (B27).
 */
export function PromotionsView({ groups, supplierPicker, onOpenPromotion }: Props) {
  const { t } = useTranslation("order")
  const [kind, setKind] = React.useState<PromoKind | "ALL">("ALL")

  const visible = kind === "ALL" ? groups : groups.filter((g) => g.kind === kind)
  const endingSoon = visible.filter((g) => {
    const d = daysUntil(g.effectiveTo)
    return d !== null && d <= ENDING_SOON_DAYS
  })

  return (
    <Box>
      <Group align="flex-end" gap="lg" mb="lg" wrap="wrap">
        <Box>
          <Text fz="var(--nw-fz-body)" fw={500} c="var(--nw-text-2)" mb={4}>
            {t("catalog.supplierLabel")}
          </Text>
          {supplierPicker}
        </Box>
        <Box>
          <Text fz="var(--nw-fz-body)" fw={500} c="var(--nw-text-2)" mb={4}>
            {t("catalog.promotions.typeLabel")}
          </Text>
          <Group gap="xs">
            {(["ALL", ...PROMO_KINDS] as const).map((k) => (
              <Button
                key={k}
                variant="default"
                radius="md"
                h={42}
                className={classes.toggleChip}
                data-active={kind === k || undefined}
                aria-pressed={kind === k}
                onClick={() => setKind(k)}
              >
                {k === "ALL" ? t("catalog.promotions.all") : t(`catalog.promoType.${k}`)}
              </Button>
            ))}
          </Group>
        </Box>
      </Group>

      {visible.length === 0 ? (
        <TableFrame>
          <ListState state="empty" title={t("catalog.promotions.empty")} description={t("catalog.promotions.emptyHint")} />
        </TableFrame>
      ) : (
        <>
          {endingSoon.length > 0 && (
            <Box mb="lg">
              <Group justify="space-between" mb="xs">
                <Text fz="var(--nw-fz-card)" fw={650}>
                  {t("catalog.promotions.endingSoon")}
                </Text>
                <Text className={classes.meta}>{t("catalog.promotions.endingSoonHint")}</Text>
              </Group>
              <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
                {endingSoon.map((g) => (
                  <Box key={g.promotionId} className={classes.promoCard} component="div">
                    <Group justify="space-between" wrap="nowrap">
                      <Box component="span" className={classes.pill}>
                        {promoIcon(g.kind)}
                        {t(`catalog.promoType.${g.kind}`)}
                      </Box>
                      <Text span className={`${classes.meta} ${classes.mono}`}>
                        {t("catalog.untilDate", { date: formatDate(g.effectiveTo) })}
                      </Text>
                    </Group>
                    <Text fz="var(--nw-fz-lead)" fw={650} lineClamp={1}>
                      {g.name || "-"}
                    </Text>
                    <Text className={classes.meta} lineClamp={1}>
                      {g.supplierNames.join(", ") || "-"}
                    </Text>
                    <Box>
                      <Ends value={g.effectiveTo} />
                    </Box>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>
          )}

          {PROMO_KINDS.filter((k) => visible.some((g) => g.kind === k)).map((k) => {
            const rows = visible.filter((g) => g.kind === k)
            return (
              <Box key={k} mb="md">
                <TableFrame>
                  <Group justify="space-between" px="md" py="sm">
                    <Box component="span" className={classes.pill}>
                      {promoIcon(k)}
                      {t(`catalog.promoType.${k}`)}
                    </Box>
                    <Text className={classes.meta}>{t("catalog.promotions.count", { count: rows.length })}</Text>
                  </Group>
                  <Table.ScrollContainer minWidth={760}>
                    <Table verticalSpacing="sm" highlightOnHover>
                      <Table.Thead className={classes.promoHead}>
                        <Table.Tr>
                          <Table.Th w={72}>{t("catalog.col.action")}</Table.Th>
                          <Table.Th>{t("catalog.promotions.colPromotion")}</Table.Th>
                          <Table.Th>{t("catalog.promotions.colSupplier")}</Table.Th>
                          <Table.Th>{t("catalog.promotions.colScope")}</Table.Th>
                          <Table.Th>{t("catalog.promotions.colEnds")}</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {rows.map((g) => (
                          <Table.Tr key={g.promotionId}>
                            <Table.Td>
                              <Tooltip label={t("catalog.promotions.openInCatalog")} withArrow>
                                <ActionIcon
                                  variant="light"
                                  color="gray"
                                  size={32}
                                  radius="md"
                                  aria-label={t("catalog.promotions.openInCatalog")}
                                  onClick={() => onOpenPromotion(g.promotionId)}
                                >
                                  <ArrowUpRightIcon size={18} />
                                </ActionIcon>
                              </Tooltip>
                            </Table.Td>
                            <Table.Td>
                              <Text fz="var(--nw-fz-body)" fw={650}>
                                {g.name || "-"}
                              </Text>
                              <Text className={classes.meta}>{describePromotion(t, g.rule)}</Text>
                            </Table.Td>
                            <Table.Td>
                              <Text fz="var(--nw-fz-body)">{g.supplierNames.join(", ") || "-"}</Text>
                            </Table.Td>
                            <Table.Td>
                              <Text fz="var(--nw-fz-body)">{t("catalog.productsCount", { count: g.products.length })}</Text>
                            </Table.Td>
                            <Table.Td>
                              <Ends value={g.effectiveTo} />
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </Table.ScrollContainer>
                </TableFrame>
              </Box>
            )
          })}

          <Box className={classes.notice}>
            <InfoIcon size={18} />
            <Text span inherit>
              {t("catalog.promotions.infoNote")}
            </Text>
          </Box>
        </>
      )}
    </Box>
  )
}
